import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { FileDropZone } from '@/components/FileDropZone';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Loader2, FileText, Paperclip, Upload, Trash2, Eye } from 'lucide-react';
import { maskBRL, parseBRL, formatBRL } from '@/lib/formatters';

const EMPTY_FORM = {
  unidade: '',
  contador_id: '',
  mes_referencia: '',
  valor: '',
};

const MESES = [
  { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' }, { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' },
];
const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 4 }, (_, i) => String(ANO_ATUAL - 3 + i));

const OutrosDocumentos = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  const [documentos, setDocumentos] = useState([]);
  const [contadores, setContadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [filtroUnidade, setFiltroUnidade] = useState('all');
  const [filtroContador, setFiltroContador] = useState('all');
  const [filtroMesSelect, setFiltroMesSelect] = useState('');
  const [filtroAnoSelect, setFiltroAnoSelect] = useState('');
  const filtroMes = filtroMesSelect && filtroAnoSelect ? `${filtroAnoSelect}-${filtroMesSelect}` : '';

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [arquivos, setArquivos] = useState([]);

  const [showArquivoModal, setShowArquivoModal] = useState(false);
  const [documentoArquivo, setDocumentoArquivo] = useState(null);
  const [novoPdf, setNovoPdf] = useState(null);
  const [novoXml, setNovoXml] = useState(null);
  const [salvandoArquivo, setSalvandoArquivo] = useState(false);

  useEffect(() => {
    if (company?.id) {
      fetchContadores();
    }
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      fetchDocumentos();
    }
  }, [company?.id, filtroUnidade, filtroContador, filtroMes]);

  const fetchContadores = async () => {
    try {
      const response = await axiosInstance.get(`/contadores/${company.id}`, { params: { ativo: true } });
      setContadores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contadores');
    }
  };

  const fetchDocumentos = async () => {
    try {
      setLoading(true);
      const params = { empresa_id: company.id };
      if (filtroUnidade !== 'all') params.unidade = filtroUnidade;
      if (filtroContador !== 'all') params.contador_id = filtroContador;
      if (filtroMes) params.mes = filtroMes;
      const response = await axiosInstance.get('/outros-documentos', { params });
      setDocumentos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const contadoresDaUnidade = formData.unidade
    ? contadores.filter((c) => c.empresas_atendidas.includes(formData.unidade))
    : [];

  const handleOpenModal = () => {
    setFormData(EMPTY_FORM);
    setArquivos([]);
    setShowModal(true);
  };

  const handleUnidadeChange = (unidade) => {
    setFormData({ ...formData, unidade, contador_id: '' });
  };

  const handleArquivosChange = (e) => {
    const files = Array.from(e.target.files || []);
    const invalidos = files.filter((f) => !f.name.toLowerCase().endsWith('.pdf'));
    if (invalidos.length > 0) {
      toast.error('Selecione apenas arquivos .pdf');
      e.target.value = '';
      return;
    }
    setArquivos(files);
  };

  const handleOpenArquivoModal = (documento) => {
    setDocumentoArquivo(documento);
    setNovoPdf(null);
    setNovoXml(null);
    setShowArquivoModal(true);
  };

  const handleNovoArquivoChange = (e, tipo) => {
    const file = e.target.files[0];
    if (!file) return;
    const extensao = tipo === 'pdf' ? '.pdf' : '.xml';
    if (!file.name.toLowerCase().endsWith(extensao)) {
      toast.error(`Selecione um arquivo ${extensao}`);
      e.target.value = '';
      return;
    }
    if (tipo === 'pdf') setNovoPdf(file);
    else setNovoXml(file);
  };

  const handleSalvarArquivo = async () => {
    if (!novoPdf && !novoXml) {
      toast.error('Selecione ao menos um arquivo (PDF ou XML)');
      return;
    }
    try {
      setSalvandoArquivo(true);
      const fd = new FormData();
      if (novoPdf) fd.append('arquivo_pdf', novoPdf);
      if (novoXml) fd.append('arquivo_xml', novoXml);

      await axiosInstance.patch(`/outros-documentos/${documentoArquivo.id}/arquivo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Arquivo atualizado com sucesso!');
      setShowArquivoModal(false);
      fetchDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar arquivo');
    } finally {
      setSalvandoArquivo(false);
    }
  };

  const handleViewDocumento = async (documento) => {
    try {
      const fileRes = await axiosInstance.get(`/outros-documentos/${documento.id}/arquivo`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(fileRes.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const handleDelete = async (documento) => {
    if (!window.confirm(`Excluir o documento "${documento.arquivo_pdf_nome}"?`)) return;
    try {
      await axiosInstance.delete(`/outros-documentos/${documento.id}`);
      toast.success('Documento excluído com sucesso!');
      fetchDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir documento');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.unidade || !formData.contador_id || !formData.mes_referencia) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (arquivos.length === 0) {
      toast.error('Selecione ao menos um arquivo PDF');
      return;
    }

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('empresa_id', company.id);
      fd.append('unidade', formData.unidade);
      fd.append('contador_id', formData.contador_id);
      fd.append('mes_referencia', formData.mes_referencia);
      if (formData.valor) fd.append('valor', String(parseBRL(formData.valor)));
      arquivos.forEach((arquivo) => fd.append('arquivos', arquivo));

      const response = await axiosInstance.post('/outros-documentos/upload-lote', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(response.data.message);
      setShowModal(false);
      fetchDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao lançar documento(s)');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="outros-documentos" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-400" />
                Outros Documentos
              </h1>
              <p className="text-zinc-400 mt-1">Envio de outros documentos para o contador</p>
            </div>
            <Button onClick={handleOpenModal} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" /> Novo Documento
            </Button>
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <Select value={filtroUnidade} onValueChange={setFiltroUnidade}>
                  <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Empresas</SelectItem>
                    <SelectItem value="matriz">Matriz</SelectItem>
                    <SelectItem value="filial">Filial</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroContador} onValueChange={setFiltroContador}>
                  <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Contador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Contadores</SelectItem>
                    {contadores.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroMesSelect} onValueChange={setFiltroMesSelect}>
                  <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroAnoSelect} onValueChange={setFiltroAnoSelect}>
                  <SelectTrigger className="w-[110px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {ANOS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Documentos Lançados</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                </div>
              ) : documentos.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum documento lançado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead>Empresa</TableHead>
                        <TableHead>Arquivo</TableHead>
                        <TableHead>Mês Ref.</TableHead>
                        <TableHead>Valor R$</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documentos.map((documento) => (
                        <TableRow key={documento.id} className="border-zinc-800 hover:bg-zinc-800/50">
                          <TableCell className="capitalize">{documento.unidade}</TableCell>
                          <TableCell>{documento.arquivo_pdf_nome}</TableCell>
                          <TableCell>{documento.mes_referencia}</TableCell>
                          <TableCell>{documento.valor != null ? formatBRL(documento.valor) : '—'}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={documento.status === 'enviada'
                                ? 'border-green-500/50 text-green-400'
                                : 'border-amber-500/50 text-amber-400'
                              }
                            >
                              {documento.status === 'enviada' ? 'Enviada' : 'Pendente'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewDocumento(documento)}
                              title="Visualizar documento"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenArquivoModal(documento)}
                              title="Substituir PDF/anexar XML"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(documento)}
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-400" />
              Novo Documento
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Empresa *</Label>
              <Select value={formData.unidade} onValueChange={handleUnidadeChange}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="matriz">Matriz</SelectItem>
                  <SelectItem value="filial">Filial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Contador *</Label>
              <Select
                value={formData.contador_id}
                onValueChange={(v) => setFormData({ ...formData, contador_id: v })}
                disabled={!formData.unidade}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder={formData.unidade ? 'Selecione' : 'Selecione a empresa primeiro'} />
                </SelectTrigger>
                <SelectContent>
                  {contadoresDaUnidade.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="mes_referencia">Mês de Referência *</Label>
              <Input
                id="mes_referencia"
                type="month"
                value={formData.mes_referencia}
                onChange={(e) => setFormData({ ...formData, mes_referencia: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                required
              />
            </div>

            <div>
              <Label htmlFor="valor">Valor R$</Label>
              <Input
                id="valor"
                type="text"
                inputMode="numeric"
                placeholder="0,00"
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: maskBRL(e.target.value) })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div>
              <Label htmlFor="arquivos">Documentos (PDF) *</Label>
              <FileDropZone onDropFiles={(files) => handleArquivosChange({ target: { files } })}>
                <Input
                  id="arquivos"
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleArquivosChange}
                  className="bg-zinc-800 border-zinc-700"
                />
                {arquivos.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {arquivos.map((f) => (
                      <p key={f.name} className="text-xs text-green-400 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" />{f.name}
                      </p>
                    ))}
                  </div>
                )}
              </FileDropZone>
              <p className="text-xs text-zinc-500 mt-1">
                Selecione ou arraste um ou mais arquivos — cada PDF vira um documento separado. O XML pode ser anexado depois, documento por documento.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Lançar Documento(s)
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showArquivoModal} onOpenChange={setShowArquivoModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-400" />
              Substituir Arquivo do Documento
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-zinc-400">{documentoArquivo?.arquivo_pdf_nome}</p>

            <div>
              <Label htmlFor="novo_pdf">Documento (PDF)</Label>
              <Input
                id="novo_pdf"
                type="file"
                accept=".pdf"
                onChange={(e) => handleNovoArquivoChange(e, 'pdf')}
                className="bg-zinc-800 border-zinc-700"
              />
              {novoPdf && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{novoPdf.name}</p>}
            </div>

            <div>
              <Label htmlFor="novo_xml">XML (opcional)</Label>
              <Input
                id="novo_xml"
                type="file"
                accept=".xml"
                onChange={(e) => handleNovoArquivoChange(e, 'xml')}
                className="bg-zinc-800 border-zinc-700"
              />
              {novoXml && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{novoXml.name}</p>}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setShowArquivoModal(false)} className="border-zinc-700">
              Cancelar
            </Button>
            <Button onClick={handleSalvarArquivo} disabled={salvandoArquivo} className="bg-orange-600 hover:bg-orange-700">
              {salvandoArquivo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutrosDocumentos;
